/**
 * Dashboard styles — imports from foundation + patterns.
 */
import {FONT, SIZE, WEIGHT} from '../../styles/foundation/typography'
import {
  NAVY, BORDER, LIGHT_BG, SUBTLE, WHITE, PRIMARY_BLUE,
  SUCCESS, ERROR, WARNING, ORANGE, STATUS_COLORS, withOpacity,
} from '../../styles/foundation/colors'
import {SHADOW} from '../../styles/foundation/effects'

export {FONT, SIZE, WEIGHT, NAVY, BORDER, LIGHT_BG, SUBTLE, WHITE, PRIMARY_BLUE, SUCCESS, ERROR, WARNING, ORANGE, SHADOW}

// ── Soft Segmented design language ──────────────────────────────
// Airy, rounded, pastel — the finalized D2C dashboard look. Reuse these so every
// card / control / chip stays consistent across the overview and analytics screens.
const SEG_TRACK = '#f1f4f8'

// Soft, rounded surface card.
export const softCardSx = {
  bgcolor: WHITE,
  border: `1px solid ${BORDER}`,
  borderRadius: '16px',
  p: {xs: 2, md: 2.5},
  mb: 2,
  boxShadow: SHADOW.card,
  transition: 'box-shadow 0.2s',
  '&:hover': {boxShadow: SHADOW.cardHover},
}

export const softCardTitleSx = {fontFamily: FONT, fontSize: SIZE.sm, fontWeight: WEIGHT.bold, color: NAVY}
export const softCardSubSx = {fontFamily: FONT, fontSize: SIZE['2xs'], color: SUBTLE, mt: 0.3}

// iOS-style segmented control. `accent` variant fills the active pill with brand blue
// (used for Forward/Reverse); the default variant raises a white pill (used for D2C/PTL).
export const segTrackSx = {display: 'inline-flex', gap: '4px', p: '4px', borderRadius: '12px', bgcolor: SEG_TRACK}
export const segItemSx = (active, accent = false) => ({
  px: 2, py: 0.9, borderRadius: '9px', cursor: 'pointer', userSelect: 'none',
  display: 'flex', alignItems: 'center', gap: 0.85,
  fontFamily: FONT, fontSize: SIZE.xs, fontWeight: WEIGHT.bold, whiteSpace: 'nowrap',
  color: active ? (accent ? WHITE : NAVY) : SUBTLE,
  bgcolor: active ? (accent ? PRIMARY_BLUE : WHITE) : 'transparent',
  boxShadow: active ? (accent ? '0 2px 6px rgba(55,131,231,0.35)' : '0 1px 3px rgba(20,35,55,0.12)') : 'none',
  transition: 'all 0.15s',
  '& svg': {fontSize: 15},
})

// Pastel KPI card + icon chip (tint derived from the accent so it always matches).
export const heroKpiIconSx = (accent = PRIMARY_BLUE) => ({
  width: 32, height: 32, borderRadius: '9px', display: 'grid', placeItems: 'center', flex: 'none',
  bgcolor: withOpacity(accent, 0.16), color: accent, '& svg': {fontSize: 17},
})

// ── Data Cockpit language (dense, bordered) — used on the Shipments tab ──
// A tighter, table-forward treatment: bordered panels, uppercase section headers,
// tabular numerals. Kept distinct from the Soft cards used on the Overview.
export const cockpitPanelSx = {
  bgcolor: WHITE, border: `1px solid ${BORDER}`, borderRadius: '10px',
  overflow: 'hidden', mb: 2, boxShadow: SHADOW.card,
}
// Turns an in-card title into a cockpit section header (uppercase, ruled underline).
export const cockpitTitleSx = {
  fontFamily: FONT, fontSize: '11px', fontWeight: WEIGHT.extra, letterSpacing: '0.07em',
  textTransform: 'uppercase', color: SUBTLE, mb: 2, pb: 1.25, borderBottom: `1px solid ${BORDER}`,
}
// Compact inline segmented control (bordered, blue active) — the cockpit variant.
export const cockpitSegSx = {display: 'inline-flex', border: `1px solid ${BORDER}`, borderRadius: '6px', overflow: 'hidden'}
export const cockpitSegItemSx = (active) => ({
  px: 1.25, py: 0.5, fontFamily: FONT, fontSize: '10.5px', fontWeight: WEIGHT.bold,
  cursor: 'pointer', userSelect: 'none', whiteSpace: 'nowrap',
  color: active ? WHITE : SUBTLE, bgcolor: active ? PRIMARY_BLUE : WHITE, transition: 'all 0.15s',
})

// KPI accents follow the fixed semantic status palette so a status reads the same colour
// in the hero cards and in every chart. Total is the neutral volume metric (brand blue).
// PTL-only cards (breached / stuck) keep their own severity colours.
export const KPI_ACCENTS = {
  totalShipments: PRIMARY_BLUE,
  pendingPickups: STATUS_COLORS.ORDER_PLACED,
  inTransit: STATUS_COLORS.INTRANSIT,
  ndrPending: STATUS_COLORS.DELIVERY_FAILED,
  rto: STATUS_COLORS.RTO,
  delivered: STATUS_COLORS.DELIVERED,
  breached: ERROR,
  stuckNonAbd: WARNING,
}

// ── Hero KPI card — Soft Segmented: pastel tint keyed to the accent ──
export const heroKpiCardSx = (accent = PRIMARY_BLUE) => ({
  p: '16px',
  borderRadius: '16px',
  border: `1px solid ${withOpacity(accent, 0.22)}`,
  bgcolor: withOpacity(accent, 0.07),
  height: '100%',
  transition: 'transform 0.2s, box-shadow 0.2s',
  '&:hover': {transform: 'translateY(-2px)', boxShadow: SHADOW.cardHover},
})

export const heroKpiValueSx = (color = NAVY) => ({
  fontFamily: FONT,
  fontWeight: WEIGHT.extra,
  fontSize: '26px',
  color,
  lineHeight: 1,
})

export const heroKpiLabelSx = {
  fontFamily: FONT,
  fontWeight: WEIGHT.semibold,
  fontSize: SIZE['2xs'],
  color: SUBTLE,
}

export const pageContainerSx = {
  fontFamily: FONT,
  p: {xs: 1.5, md: 2.5},
  bgcolor: LIGHT_BG,
  minHeight: '100vh',
}

export const sectionCardSx = {
  p: {xs: 2, md: 2.5},
  borderRadius: '10px',
  border: `1px solid ${BORDER}`,
  bgcolor: WHITE,
  mb: 2.5,
  boxShadow: SHADOW.card,
}

export const sectionTitleSx = {
  fontFamily: FONT,
  fontWeight: WEIGHT.bold,
  fontSize: SIZE.xl,
  color: NAVY,
  mb: 1.5,
}

export const tabBarSx = {
  borderBottom: `1px solid ${BORDER}`,
  mb: 2,
  '& .MuiTab-root': {
    fontFamily: FONT,
    fontWeight: WEIGHT.semibold,
    fontSize: SIZE.sm,
    textTransform: 'none',
    minHeight: 44,
  },
  '& .Mui-selected': {color: `${PRIMARY_BLUE} !important`},
  '& .MuiTabs-indicator': {bgcolor: PRIMARY_BLUE},
}
